import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import FacebookIcon from '@mui/icons-material/Facebook';

import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import WebhookIcon from '@mui/icons-material/Webhook';
import {
  Box,
  Chip,
  Stack,
  SvgIconProps,
  SxProps,
  Theme,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import NewActionButton from 'components/Search/components/SearchAdvance/components/Shared/NewActionButton';

import { SelectDropdownInput } from 'components/Inputs/Dropdown/SelectDropdown/SelectDropdownInput';
import { useGetFetchAndFormatBroadcastMessages } from 'components/Search/components/SearchAdvance/components/hooks';
import { useSearch } from 'components/Search/SearchContextProvider';

import { TSessionConfigData, useCosmos } from 'providers/contexts/CosmosContext';
import { BroadcastChannelItem } from 'providers/contexts/extensions/Broadcast';
import {
  ComponentType,
  memo,
  useMemo,
  useState,
  useRef,
  useCallback,
  useLayoutEffect,
} from 'react';

import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { bloxValues } from 'themes/constants';
import {
  checkBroadcastChangeSettingsPermission,
  checkBroadcastFullAccessPermission,
  checkSiteAdminPermission,
  PermissionInfo,
} from 'utility/permissions';
import { getColorMix } from 'utility/styles';

import { useChannelsModalContext } from './ChannelsModalContext';

// ---------------------------------------------------------
// Permission Check
// ---------------------------------------------------------

export function checkChannelManagePerms(
  sessionConfig?: TSessionConfigData,
): PermissionInfo {
  const { granted: userIsAdmin } = checkSiteAdminPermission(sessionConfig);
  const { granted: userHasFullAccess } =
    checkBroadcastFullAccessPermission(sessionConfig);
  const { granted: userHasChangeSettings, name: changeSettingsPermName } =
    checkBroadcastChangeSettingsPermission(sessionConfig);

  if (userIsAdmin || userHasFullAccess || userHasChangeSettings) {
    return {
      info: undefined,
      disabled: false,
    };
  }

  return {
    info: changeSettingsPermName,
    disabled: true,
  };
}

/**
 * Custom hook that wraps checkChannelManagePerms with translated error messages
 * @returns PermissionInfo with translated info message
 */
export function useChannelManagePerms(): PermissionInfo {
  const { t } = useTranslation();
  const { sessionConfig } = useCosmos();

  return useMemo(() => {
    const perms = checkChannelManagePerms(sessionConfig);
    return {
      ...perms,
      info: perms.info
        ? t('blox.permissions.msg.required_short', { name: perms.info })
        : undefined,
    };
  }, [sessionConfig, t]);
}

// ---------------------------------------------------------
// Channel Status Badge Component (Modern Design)
// ---------------------------------------------------------

export type ChannelStatusBadgeProps = {
  channel?: BroadcastChannelItem | null;
  compact?: boolean;
  propSx?: SxProps<Theme>;
};

/**
 * ChannelStatusBadge Component
 * Modern chip design for channel enabled/disabled status
 */
export const ChannelStatusBadge = memo(
  ({ channel, compact, propSx }: ChannelStatusBadgeProps) => {
    const { t } = useTranslation();
    const theme = useTheme();

    const isEnabled = !channel?.disabled;
    const strStatus = isEnabled
      ? t('blox.notifier.channel.enabled')
      : t('blox.notifier.channel.disabled');
    const Icon = isEnabled ? CheckCircleOutlineIcon : DoNotDisturbIcon;
    const statusColor = isEnabled
      ? theme.palette.success.main
      : theme.palette.text.disabled;

    const statusBadgeSx: SxProps<Theme> = useMemo(() => {
      const isDarkMode = theme?.palette?.mode === 'dark';
      const blackWhite = isDarkMode
        ? theme.palette.common.black
        : theme.palette.common.white;

      const valFontSize = compact
        ? bloxValues.tinyFontSizeRem
        : bloxValues.smallFontSizeRem;
      const valPadding = compact ? '2px' : '4px';
      const valIconFontSize = compact
        ? bloxValues.tinyFontSizeRem
        : bloxValues.regularFontSizeRem;

      return {
        color: statusColor,
        borderColor: statusColor,
        bgcolor: getColorMix(statusColor, 10, blackWhite, 'srgb'),
        fontSize: valFontSize,
        fontWeight: bloxValues.fontWeightMedium,
        px: valPadding,
        textTransform: 'capitalize',
        '.MuiChip-icon': {
          color: 'inherit',
          fontSize: valIconFontSize,
        },
        ...(typeof propSx === 'function' ? propSx(theme) : propSx),
      };
    }, [compact, propSx, statusColor, theme]);

    if (!channel) {
      return null;
    }

    return (
      <Chip
        data-testid="channel-status-badge"
        icon={<Icon />}
        label={strStatus}
        size="small"
        variant="filled"
        sx={statusBadgeSx}
      />
    );
  },
);
ChannelStatusBadge.displayName = 'ChannelStatusBadge';

// ---------------------------------------------------------
// Delivery Target Info Helper
// ---------------------------------------------------------

type TargetInfo = {
  label: string;
  icon: ComponentType<SvgIconProps>;
  color: string;
};

type GetTargetInfoArgs = {
  driver: string;
  name?: string;
  theme: Theme;
  t: TFunction;
};

function getTargetInfo({ driver, name, theme, t }: GetTargetInfoArgs): TargetInfo {
  const driverLower = driver?.toLowerCase() ?? '';

  switch (driverLower) {
    case 'appmessages':
    case 'mobileapp':
      return {
        label: name || t('blox.notifier.channel.targets.mobile_app'),
        icon: PhoneAndroidIcon,
        color: theme.palette.info.main,
      };
    case 'facebook':
      return {
        label: name || t('blox.notifier.channel.targets.facebook'),
        icon: FacebookIcon,
        color: theme.palette.brands.facebook.main,
      };
    case 'webhook':
      return {
        label: name || t('blox.notifier.channel.targets.webhook'),
        icon: WebhookIcon,
        color: theme.palette.warning.main,
      };
    case 'pushnotification':
      return {
        label: name || t('blox.notifier.channel.targets.push_notification'),
        icon: NotificationsActiveIcon,
        color: theme.palette.success.main,
      };
    default:
      return {
        label: name || driver,
        icon: NotificationsActiveIcon,
        color: theme.palette.text.secondary,
      };
  }
}

// ---------------------------------------------------------
// Channel Delivery Targets Cell (Modern Design)
// ---------------------------------------------------------

export type ChannelDeliveryTargetsCellProps = {
  channel?: BroadcastChannelItem | null;
  compact?: boolean;
  propSx?: SxProps<Theme>;
};

// Estimated chip width for calculation (icon + padding + some label)
const CHIP_BASE_WIDTH = 120; // approximate width per chip
const CHIP_GAP = 4; // gap between chips
const OVERFLOW_CHIP_WIDTH = 40; // width of +N chip

/**
 * ChannelDeliveryTargetsCell Component
 * Modern display for delivery targets in grid with chip badges
 * Dynamically calculates how many chips fit, showing +N for overflow
 */
export const ChannelDeliveryTargetsCell = memo(
  ({ channel, compact, propSx }: ChannelDeliveryTargetsCellProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const targets = channel?.targets || [];
    const containerRef = useRef<HTMLDivElement>(null);
    const [maxVisible, setMaxVisible] = useState(targets.length);

    const isDarkMode = theme?.palette?.mode === 'dark';
    const blackWhite = isDarkMode
      ? theme.palette.common.black
      : theme.palette.common.white;

    // Calculate how many chips can fit in the container
    const calculateVisibleChips = useCallback(() => {
      if (!containerRef.current || targets.length === 0) return;

      const containerWidth = containerRef.current.offsetWidth;
      const chipWidth = compact ? 100 : CHIP_BASE_WIDTH;

      // Calculate how many chips fit
      let availableWidth = containerWidth;
      let visibleCount = 0;

      for (let i = 0; i < targets.length; i++) {
        const neededWidth = chipWidth + (i > 0 ? CHIP_GAP : 0);
        const remainingItems = targets.length - i - 1;

        // If this isn't the last item, we need to reserve space for +N chip
        if (remainingItems > 0) {
          const spaceWithOverflow = neededWidth + CHIP_GAP + OVERFLOW_CHIP_WIDTH;
          if (availableWidth >= spaceWithOverflow) {
            availableWidth -= neededWidth;
            visibleCount++;
          } else {
            // Can't fit this chip + overflow indicator
            break;
          }
        } else {
          // Last item - just check if it fits
          if (availableWidth >= neededWidth) {
            visibleCount++;
          }
        }
      }

      // Always show at least 1 chip if we have targets
      setMaxVisible(Math.max(1, visibleCount));
    }, [targets.length, compact]);

    // Use ResizeObserver to detect container size changes
    useLayoutEffect(() => {
      if (!containerRef.current) return;

      const resizeObserver = new window.ResizeObserver(() => {
        calculateVisibleChips();
      });

      resizeObserver.observe(containerRef.current);
      calculateVisibleChips(); // Initial calculation

      return () => {
        resizeObserver.disconnect();
      };
    }, [calculateVisibleChips]);

    if (targets.length === 0) {
      return null;
    }

    const valFontSize = compact
      ? bloxValues.tinyFontSizeRem
      : bloxValues.smallFontSizeRem;
    const valIconFontSize = compact
      ? bloxValues.tinyFontSizeRem
      : bloxValues.regularFontSizeRem;

    // Determine visible and overflow targets
    const visibleTargets = targets.slice(0, maxVisible);
    const overflowTargets = targets.slice(maxVisible);
    const overflowCount = overflowTargets.length;

    // Build overflow tooltip content
    const overflowTooltipContent = overflowTargets
      .map((target) => {
        const info = getTargetInfo({
          driver: target.driver,
          name: target.name,
          theme,
          t,
        });
        return info.label;
      })
      .join(', ');

    return (
      <Stack
        ref={containerRef}
        direction="row"
        spacing={0.5}
        sx={{
          flexWrap: 'nowrap',
          gap: 0.5,
          alignItems: 'center',
          overflow: 'hidden',
          width: '100%',
          ...(typeof propSx === 'function' ? propSx(theme) : propSx),
        }}
      >
        {visibleTargets.map((target, index) => {
          const targetInfo = getTargetInfo({
            driver: target.driver,
            name: target.name,
            theme,
            t,
          });
          const Icon = targetInfo.icon;
          const statusColor = targetInfo.color;

          return (
            <Tooltip
              key={`target-${index}-${target?.id}`}
              title={targetInfo.label}
              enterDelay={300}
            >
              <Chip
                icon={<Icon />}
                label={targetInfo.label}
                size="small"
                variant="filled"
                sx={{
                  flexShrink: 0,
                  color: statusColor,
                  borderColor: statusColor,
                  bgcolor: getColorMix(statusColor, 10, blackWhite, 'srgb'),
                  fontSize: valFontSize,
                  fontWeight: bloxValues.fontWeightMedium,
                  maxWidth: compact ? 100 : 140,
                  '.MuiChip-label': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  },
                  '.MuiChip-icon': {
                    color: 'inherit',
                    fontSize: valIconFontSize,
                  },
                }}
              />
            </Tooltip>
          );
        })}
        {overflowCount > 0 && (
          <Tooltip
            title={overflowTooltipContent}
            enterDelay={300}
          >
            <Chip
              label={`+${overflowCount}`}
              size="small"
              variant="filled"
              sx={{
                flexShrink: 0,
                color: theme.palette.text.secondary,
                borderColor: theme.palette.divider,
                bgcolor: getColorMix(
                  theme.palette.text.secondary,
                  10,
                  blackWhite,
                  'srgb',
                ),
                fontSize: valFontSize,
                fontWeight: bloxValues.fontWeightMedium,
                minWidth: 'auto',
              }}
            />
          </Tooltip>
        )}
      </Stack>
    );
  },
);
ChannelDeliveryTargetsCell.displayName = 'ChannelDeliveryTargetsCell';

// ---------------------------------------------------------
// Channel Rules Count Badge (Modern Design)
// ---------------------------------------------------------

export type ChannelRulesCountBadgeProps = {
  channel?: BroadcastChannelItem | null;
  compact?: boolean;
  propSx?: SxProps<Theme>;
};

/**
 * ChannelRulesCountBadge Component
 * Modern chip design for showing rules count
 */
export const ChannelRulesCountBadge = memo(
  ({ channel, compact, propSx }: ChannelRulesCountBadgeProps) => {
    const theme = useTheme();

    const rules = channel?.publish_rules || [];
    const count = rules.length;
    const hasRules = count > 0;
    const strLabel = `${count} ${count === 1 ? 'rule' : 'rules'}`;
    const statusColor = hasRules
      ? theme.palette.primary.main
      : theme.palette.text.disabled;

    const badgeSx: SxProps<Theme> = useMemo(() => {
      const isDarkMode = theme?.palette?.mode === 'dark';
      const blackWhite = isDarkMode
        ? theme.palette.common.black
        : theme.palette.common.white;

      const valFontSize = compact
        ? bloxValues.tinyFontSizeRem
        : bloxValues.smallFontSizeRem;
      const valPadding = compact ? '2px' : '4px';

      return {
        color: statusColor,
        borderColor: statusColor,
        bgcolor: hasRules
          ? getColorMix(statusColor, 10, blackWhite, 'srgb')
          : 'transparent',
        fontSize: valFontSize,
        fontWeight: bloxValues.fontWeightMedium,
        px: valPadding,
        ...(typeof propSx === 'function' ? propSx(theme) : propSx),
      };
    }, [compact, hasRules, propSx, statusColor, theme]);

    if (!channel) {
      return null;
    }

    return (
      <Chip
        data-testid="channel-rules-badge"
        label={strLabel}
        size="small"
        variant={hasRules ? 'filled' : 'outlined'}
        sx={badgeSx}
      />
    );
  },
);
ChannelRulesCountBadge.displayName = 'ChannelRulesCountBadge';

// ---------------------------------------------------------
// Pre-Search Content (Create Channel Button)
// ---------------------------------------------------------
// Channel Status Filter (inline filter for pre-search area)
// ---------------------------------------------------------

const getFilterFromCtrlSx = (theme: Theme, hasValue: boolean): SxProps<Theme> => {
  const activeColor = hasValue ? theme.palette.primary.main : theme.palette.grey[500];
  return {
    width: 125,
    height: 36,
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: activeColor,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: hasValue ? theme.palette.primary.dark : theme.palette.text.primary,
    },
  };
};

const getFilterSelectSx = (theme: Theme, hasValue: boolean): SxProps<Theme> => {
  return {
    height: 36,
    '& .MuiSelect-select': {
      py: 0.75,
      px: 1,
      fontSize: bloxValues.smallFontSizeRem,
      fontWeight: bloxValues.fontWeightMedium,
      color: hasValue ? theme.palette.primary.main : theme.palette.text.secondary,
    },
  };
};

/**
 * ChannelsStatusFilter
 * Inline SelectDropdownInput for filtering channels by enabled/disabled status.
 * Uses staticChoices with the same pattern as ServicesFilterFields / NotifierPopoverFields.
 * Reads/writes the `disabled` query param via the search context.
 */
export const ChannelsStatusFilter = memo(() => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { validListFilter, updateQueryParams } = useSearch();

  // Current filter value from URL query params
  const currentValue =
    typeof validListFilter?.disabled === 'string' ? validListFilter.disabled : '';

  const hasValue = Boolean(currentValue);

  // Translation strings
  const strEnabled = t('blox.notifier.channel.enabled');
  const strDisabled = t('blox.notifier.channel.disabled');
  const strStatus = t('blox.general.status');

  const statusChoices = useMemo(
    () => [
      { value: 'false', label: strEnabled },
      { value: 'true', label: strDisabled },
    ],
    [strEnabled, strDisabled],
  );

  const handleChange = useCallback(
    (_event: any, newValue: string | string[]) => {
      const value = Array.isArray(newValue) ? newValue[0] : newValue;
      updateQueryParams((prev: URLSearchParams) => {
        if (value) {
          prev.set('disabled', value);
        } else {
          prev.delete('disabled');
        }
        return prev;
      });
    },
    [updateQueryParams],
  );

  const quickFilterFormControlSx: SxProps<Theme> = useMemo(
    () => getFilterFromCtrlSx(theme, hasValue),
    [theme, hasValue],
  );

  const quickFilterSelectSx: SxProps<Theme> = useMemo(
    () => getFilterSelectSx(theme, hasValue),
    [theme, hasValue],
  );

  return (
    <SelectDropdownInput
      data-testid="channels-status-filter"
      staticChoices={statusChoices}
      inputLabel={strStatus}
      value={currentValue}
      updateValue={handleChange}
      popoverMatchesFormControlWidth={true}
      applyChangesOnPopoverClose={false}
      infiniteScroll={false}
      showFilter={false}
      showFooter={false}
      showChipBox={false}
      multiple={false}
      renderValueDisplay="label"
      formControlVariant="outlined"
      formControlSx={quickFilterFormControlSx}
      selectSx={quickFilterSelectSx}
      popoverPaperSx={{ width: 250 }}
    />
  );
});
ChannelsStatusFilter.displayName = 'ChannelsStatusFilter';

// ---------------------------------------------------------
// Channel Message ID Filter (inline dropdown for pre-search area)
// ---------------------------------------------------------

/**
 * ChannelsMessageIdFilter
 * Inline SelectDropdownInput for filtering channels by message.
 * Uses the same pattern as the channels dropdown in NotifierSearchComponents.
 * Reads/writes the `message_id` query param via the search context.
 */
export const ChannelsMessageIdFilter = memo(() => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { validListFilter, updateQueryParams } = useSearch();
  const { fetchAndFormatBroadcastMessages } = useGetFetchAndFormatBroadcastMessages();

  // Current filter value from URL query params
  const currentValue =
    typeof validListFilter?.message_id === 'string' ? validListFilter.message_id : '';

  const hasValue = Boolean(currentValue);

  // Translation strings
  const strLabel = t('blox.notifier.channel.filter_message_id');

  const handleChange = useCallback(
    (_event: any, newValue: string | string[]) => {
      const value = Array.isArray(newValue) ? newValue[0] : newValue;
      updateQueryParams((prev: URLSearchParams) => {
        if (value) {
          prev.set('message_id', value);
        } else {
          prev.delete('message_id');
        }
        return prev;
      });
    },
    [updateQueryParams],
  );

  const quickFilterFormControlSx: SxProps<Theme> = useMemo(
    () => getFilterFromCtrlSx(theme, hasValue),
    [theme, hasValue],
  );

  const quickFilterSelectSx: SxProps<Theme> = useMemo(
    () => getFilterSelectSx(theme, hasValue),
    [theme, hasValue],
  );

  return (
    <SelectDropdownInput
      data-testid="channels-message-id-filter"
      inputLabel={strLabel}
      value={currentValue}
      updateValue={handleChange}
      fetchDataFn={fetchAndFormatBroadcastMessages}
      popoverMatchesFormControlWidth={true}
      applyChangesOnPopoverClose={false}
      infiniteScroll={true}
      showFilter={true}
      showFooter={true}
      showChipBox={false}
      multiple={false}
      renderValueDisplay="label"
      formControlVariant="outlined"
      formControlSx={quickFilterFormControlSx}
      selectSx={quickFilterSelectSx}
      popoverPaperSx={{ width: 325 }}
    />
  );
});
ChannelsMessageIdFilter.displayName = 'ChannelsMessageIdFilter';

// ---------------------------------------------------------
// Pre-Search Content Component
// ---------------------------------------------------------

export const ChannelsPreSearchContent = memo(() => {
  const { t } = useTranslation();
  const { handleOpenCreateChannelModal } = useChannelsModalContext();

  const strNew = t('blox.actions.new');
  const managePerms = useChannelManagePerms();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleCreateChannel = () => {
    if (managePerms.disabled) return;
    handleOpenCreateChannelModal();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        width: '100%',
      }}
    >
      <Box sx={{ flex: 'none' }}>
        <NewActionButton
          id="channels-create-button"
          ariaLabel="channels-create-button"
          onClick={handleCreateChannel}
          open={false}
          disabled={managePerms.disabled}
          tooltip={managePerms.info || ''}
          showArrow={false}
        >
          {strNew}
        </NewActionButton>
      </Box>

      <Box
        sx={
          isMobile
            ? {
                display: 'flex',
                gap: 1,
                alignItems: 'center',
                width: '100%',
                '& > *': { flex: 1, minWidth: 0 },
              }
            : { display: 'flex', gap: 1, alignItems: 'center' }
        }
      >
        <ChannelsStatusFilter />
        <ChannelsMessageIdFilter />
      </Box>
    </Box>
  );
});
ChannelsPreSearchContent.displayName = 'ChannelsPreSearchContent';
